import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import PlanSelectionView from "../RecommendedPlan/PlanSelectionView";
import {
  COMPARISON_COLUMNS,
  COMPARISON_ROWS,
  PLAN_DEFS,
} from "../RecommendedPlan/RecommendedPlan";
import { checkoutPlan, fetchPlanProducts } from "../../lib/planCheckout";

const NewAgentPlanScreen = ({ onBack, returnTo = "/business-step" }) => {
  const [billingInterval, setBillingInterval] = useState("month");
  const [selectedPlanId, setSelectedPlanId] = useState("scaler");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [showComparison, setShowComparison] = useState(false);
  const comparisonRef = useRef(null);
  const paymentInFlight = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const loadPlans = async () => {
      let currency = "usd";
      try {
        const response = await axios.get("https://ipinfo.io/json", { timeout: 4000 });
        currency = ({ US: "usd", CA: "cad", AU: "aud", GB: "gbp", IN: "inr" })[
          response.data?.country
        ] || "usd";
      } catch {
        // Keep USD as the display fallback when geolocation is unavailable.
      }

      const result = await fetchPlanProducts();
      if (cancelled) return;
      const plans = result
        .map((product) => {
          const matchedData = product.data?.find((item) => item.id === product.id);
          const prices = (product.prices || []).map((price) => {
            const option = price.currency_options?.find((item) => item.currency === currency);
            return option
              ? { ...price, unit_amount: option.unit_amount, currency: option.currency }
              : price;
          });
          return {
            ...product,
            prices,
            metadata: matchedData?.metadata || product.metadata || {},
          };
        })
        .filter((product) => (product.name || "").trim().toLowerCase() in PLAN_DEFS)
        .sort((a, b) =>
          PLAN_DEFS[a.name.trim().toLowerCase()].order -
          PLAN_DEFS[b.name.trim().toLowerCase()].order,
        );

      setProducts(plans);
      if (!plans.some((product) => product.name.trim().toLowerCase() === "scaler")) {
        setSelectedPlanId(plans[0]?.name?.trim().toLowerCase() || "");
      }
      if (plans.length === 0) setError("No plans available right now.");
      setLoading(false);
    };

    loadPlans().catch(() => {
      if (!cancelled) {
        setError("Failed to load plans.");
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const cards = useMemo(
    () => products.map((product) => {
      const key = product.name.trim().toLowerCase();
      const monthly = product.prices.find((price) => price.interval === "month");
      const yearly = product.prices.find((price) => price.interval === "year");
      const displayPrice = billingInterval === "year" && yearly ? yearly : monthly;
      return {
        id: key,
        name: product.name,
        icon: PLAN_DEFS[key].icon,
        summary: PLAN_DEFS[key].summary,
        recommended: !!PLAN_DEFS[key].recommended,
        minutes: monthly?.metadata?.["minutes-month"] || product.metadata?.["minutes-month"] || null,
        display: displayPrice
          ? {
              amount: displayPrice.unit_amount / 100 / (billingInterval === "year" && yearly ? 12 : 1),
              currency: displayPrice.currency,
            }
          : null,
        raw: product,
      };
    }),
    [billingInterval, products],
  );
  const selectedCard =
    cards.find((card) => card.id === selectedPlanId) ||
    cards.find((card) => card.recommended) ||
    cards[0] ||
    null;

  const revealPlanFeatures = (card) => {
    if (showComparison && selectedPlanId === card.id) {
      setShowComparison(false);
      return;
    }
    setError("");
    setSelectedPlanId(card.id);
    setShowComparison(true);
    window.requestAnimationFrame(() => {
      comparisonRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const handleContinue = async () => {
    if (paymentInFlight.current || !selectedCard) return;
    paymentInFlight.current = true;
    setCheckoutLoading(true);
    setError("");
    try {
      const ok = await checkoutPlan({
        planName: selectedCard.name,
        yearly: billingInterval === "year",
        products,
        forNewAgent: true,
        next: returnTo,
        returnParams: { paidNewAgent: "1" },
      });
      if (!ok) setError("We couldn't start the payment. Please try again.");
    } catch {
      setError("We couldn't start the payment. Please try again.");
    } finally {
      paymentInFlight.current = false;
      setCheckoutLoading(false);
    }
  };

  const getCurrencySymbol = (currency) => {
    try {
      return new Intl.NumberFormat("en", {
        style: "currency",
        currency: (currency || "usd").toUpperCase(),
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).formatToParts(1).find((part) => part.type === "currency")?.value || "$";
    } catch {
      return "$";
    }
  };

  return (
    <PlanSelectionView
      showBackBtn
      handleGoBack={onBack}
      isUpgradeFlow={false}
      setTrialModalOpen={() => {}}
      billingInterval={billingInterval}
      setBillingInterval={setBillingInterval}
      loading={loading}
      error={error}
      cards={cards}
      selectedPlanId={selectedPlanId}
      getCurrencySymbol={getCurrencySymbol}
      setError={setError}
      setSelectedPlanId={setSelectedPlanId}
      formatPrice={(amount) => Number.isFinite(amount) ? Math.round(amount).toLocaleString() : "0"}
      revealPlanFeatures={revealPlanFeatures}
      showComparison={showComparison}
      comparisonRef={comparisonRef}
      selectedCard={selectedCard}
      COMPARISON_ROWS={COMPARISON_ROWS}
      COMPARISON_COLUMNS={COMPARISON_COLUMNS}
      handleContinue={handleContinue}
      checkoutLoading={checkoutLoading}
      agentName="Your new AI agent"
    />
  );
};

export default NewAgentPlanScreen;
